import { useState } from 'react';
import { Link } from 'react-router';

import { useComponents, useProposal, useQueue } from '@/api/queries';
import { NotFoundPage } from '@/app/errors';
import { useNow } from '@/app/preferences';
import { proposalPath, runsPath, useScopeParam } from '@/app/scope';
import { Button } from '@/components/Button';
import { BlindBadge, HealthBadge, ProposalStatusBadge } from '@/components/badges';
import { ArrowRight, CheckCircle, DisclosureChevron, Warning } from '@/components/icons';
import { Leaving } from '@/components/Leaving';
import { DIALOG_LINGER_MS, useLeavingList, useLingering } from '@/components/motion';
import {
  EmptyState,
  ErrorNotice,
  Loading,
  Notice,
  Panel,
  SectionTitle,
} from '@/components/primitives';
import { Reveal } from '@/components/Reveal';
import { Timestamp } from '@/components/Timestamp';
import { SCOPE_LABEL } from '@/domain/health';
import { formatDuration } from '@/domain/time';
import type { ComponentView, Proposal, Scope } from '@/domain/types';

import { ConflictDialog } from '../proposals/dialogs';
import { EvidenceList } from '../proposals/EvidenceList';
import { conflictOf, type Conflict } from '../proposals/conflicts';
import { ProposalActions } from '../proposals/ProposalActions';
import {
  SECTION_TITLE,
  kindLabel,
  problemSince,
  reasonLabel,
  recoveryOverdue,
  sectionOf,
  type QueueSection,
} from './queue';

const ORDER: QueueSection[] = ['failures', 'recoveries', 'failed_publishes'];
const NONE: Proposal[] = [];

/** The approval queue (TDD §5.3): oldest first, in three groups, each card carrying its evidence. */
export function QueuePage() {
  const scope = useScopeParam();
  if (!scope) return <NotFoundPage />;
  return <Queue scope={scope} />;
}

function Queue({ scope }: { scope: Scope }) {
  const queue = useQueue(scope);
  const components = useComponents(scope);
  const now = useNow();
  const [conflict, setConflict] = useState<Conflict | null>(null);
  const shownConflict = useLingering(conflict, DIALOG_LINGER_MS);
  // A decided proposal leaves the queue on the next fetch; its card fades and closes up rather
  // than vanishing, so the eye can follow which one went.
  const { entries, left } = useLeavingList(queue.data ?? NONE, (p) => p.proposal_id);

  if (queue.isPending) return <Loading />;
  if (queue.isError) {
    return (
      <ErrorNotice
        message={`Could not load the queue: ${queue.error.message}`}
        action={
          <Button size="sm" onClick={() => queue.refetch()}>
            Try again
          </Button>
        }
      />
    );
  }

  const byKey = new Map(
    (components.data?.components ?? []).map((c) => [c.config.capability_id, c]),
  );
  const sections = ORDER.map((section) => ({
    section,
    items: entries.filter((entry) => sectionOf(entry.item) === section),
  })).filter((s) => s.items.length > 0);

  if (sections.length === 0) {
    return (
      <Panel bodyClassName="p-0">
        <EmptyState
          icon={<CheckCircle size={24} />}
          title={`Nothing needs a decision in ${SCOPE_LABEL[scope]}`}
        />
      </Panel>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      {sections.map(({ section, items }) => (
        <section key={section} className="flex flex-col gap-3">
          <SectionTitle count={items.length}>{SECTION_TITLE[section]}</SectionTitle>
          <div className="flex flex-col -mt-3">
            {items.map(({ key, item: proposal, leaving }) => (
              <Leaving key={key} id={key} leaving={leaving} onLeft={left} className="pt-3">
                <ProposalCard
                  proposal={proposal}
                  view={byKey.get(proposal.capability_id)}
                  now={now}
                  scope={scope}
                  onConflict={setConflict}
                />
              </Leaving>
            ))}
          </div>
        </section>
      ))}
      {shownConflict ? (
        <ConflictDialog
          before={shownConflict.before}
          current={shownConflict.current}
          displayName={shownConflict.displayName}
          open={conflict !== null}
          onClose={() => setConflict(null)}
        />
      ) : null}
    </div>
  );
}

function ProposalCard({
  proposal,
  view,
  now,
  scope,
  onConflict,
}: {
  proposal: Proposal;
  view: ComponentView | undefined;
  now: number;
  scope: Scope;
  onConflict: (conflict: Conflict) => void;
}) {
  const [showEvidence, setShowEvidence] = useState(false);
  const displayName = view?.config.display_name ?? proposal.capability_id;
  const overdue = recoveryOverdue(proposal, view?.config, now);
  const blind = Boolean(view?.state.blind_since);
  const since = problemSince(proposal);

  // The attention edge says what the Notice below spells out: overdue, or a publish that failed.
  const edge = proposal.status === 'publish_failed' ? 'failed' : overdue ? 'impaired' : undefined;

  return (
    <Panel
      as="article"
      bodyClassName="p-0"
      edge={edge}
      aria-labelledby={`card-${proposal.proposal_id}`}
    >
      <div className="flex flex-wrap items-start gap-x-6 gap-y-3 px-5 pt-4 pb-3">
        <div className="min-w-[16rem] flex-1">
          <h3
            id={`card-${proposal.proposal_id}`}
            className="text-title font-medium flex flex-wrap items-center gap-x-2 gap-y-1"
          >
            <Link
              to={proposalPath(proposal.proposal_id)}
              className="text-ink no-underline hover:underline"
            >
              {displayName}
            </Link>
            <span className="text-body font-normal text-ink-3">{kindLabel(proposal)}</span>
            <ProposalStatusBadge status={proposal.status} />
          </h3>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <HealthBadge health={proposal.from_status} />
            <ArrowRight size={14} className="text-ink-3" />
            <HealthBadge health={proposal.to_status} />
          </div>
          <p className="mt-2 text-body text-ink-2 flex flex-wrap gap-x-3 gap-y-0.5">
            <span>{reasonLabel(proposal.reason)}</span>
            <span>
              Problem for {formatDuration(now - Date.parse(since))}, since{' '}
              <Timestamp iso={since} mode="auto" />
            </span>
            <span>
              Proposed <Timestamp iso={proposal.created_at} mode="auto" />
              {proposal.created_by.startsWith('system:') ? null : ` by ${proposal.created_by}`}
            </span>
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <ProposalActions
            proposal={proposal}
            displayName={displayName}
            showDetailsLink
            onOutcome={(outcome) => {
              const c = conflictOf(outcome, proposal, displayName);
              if (c) onConflict(c);
            }}
          />
        </div>
      </div>

      {blind ||
      proposal.had_gap ||
      overdue ||
      proposal.status === 'publish_failed' ||
      view?.state.publish_uncertain ? (
        <div className="px-5 pb-3 flex flex-col gap-2">
          {proposal.status === 'publish_failed' ? (
            <Notice tone="danger" className="flex items-start gap-2">
              <Warning size={14} className="shrink-0 mt-0.5" />
              <span>
                The publish failed{proposal.last_error ? `: ${proposal.last_error}` : ''}. Nothing
                retries on its own.
              </span>
            </Notice>
          ) : null}
          {blind ? (
            <Notice tone="warning" className="flex items-start gap-2">
              <BlindBadge className="shrink-0" />
              <span>
                No run since{' '}
                {view?.state.last_observed_at ? (
                  <Timestamp iso={view.state.last_observed_at} mode="auto" />
                ) : (
                  'it was configured'
                )}
                .
              </span>
            </Notice>
          ) : null}
          {proposal.had_gap ? (
            <Notice tone="warning">A blind period falls inside the evidence.</Notice>
          ) : null}
          {overdue && view ? (
            <Notice tone="warning" className="flex items-start gap-2">
              <Warning size={14} className="shrink-0 mt-0.5" />
              <span>
                Waiting longer than the {view.config.recovery_approval_alert_min} minutes allowed;
                customers still see{' '}
                {view.state.published_status === 'ok' ? 'Operational' : 'a fault'}.
              </span>
            </Notice>
          ) : null}
          {view?.state.publish_uncertain ? (
            <Notice tone="warning">
              The last write to the page got no response; it may have landed. Retry is safe.
            </Notice>
          ) : null}
        </div>
      ) : null}

      <div className="border-t border-line px-5 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-body">
        <button
          type="button"
          aria-expanded={showEvidence}
          onClick={() => setShowEvidence((v) => !v)}
          className="inline-flex items-center gap-1.5 text-ink-2 transition-tone hover:text-ink"
        >
          <DisclosureChevron open={showEvidence} size={14} />
          {proposal.evidence.length === 0
            ? 'No evidence runs'
            : `${showEvidence ? 'Hide' : 'Show'} ${proposal.evidence.length} evidence ${proposal.evidence.length === 1 ? 'run' : 'runs'}`}
        </button>
        <Link to={runsPath(scope, proposal.capability_id)}>Run history</Link>
      </div>
      <Reveal open={showEvidence}>
        <CardEvidence proposal={proposal} checksOrder={view?.config.checks} />
      </Reveal>
    </Panel>
  );
}

/** Evidence with checks needs the proposal detail; fetch it only when a card is expanded. */
function CardEvidence({
  proposal,
  checksOrder,
}: {
  proposal: Proposal;
  checksOrder: string[] | undefined;
}) {
  const detail = useProposal(proposal.proposal_id);
  return (
    <div className="px-5 pb-4">
      {detail.isPending ? <Loading label="Loading the runs…" className="py-3" /> : null}
      <EvidenceList
        evidence={proposal.evidence}
        runs={detail.data?.evidence_runs}
        hadGap={proposal.had_gap}
        checksOrder={checksOrder}
      />
    </div>
  );
}
